/**
 * FsmNodes — React Flow ports of the three Rappid FSM state shapes (phase 6d).
 *
 * Pixel-faithful re-implementation of `src/lib/joint/shapes.js`
 * (`merode.InitialState`, `merode.State`, `merode.FinalState`) + the JointJS
 * `standard.Circle` / `standard.Ellipse` defaults they extend. The exact geometry
 * the old JointJS shapes produced (verified against rappid/dist/rappid.min.js):
 *
 *  - InitialState (`standard.Circle`): a circle, `refCx/refCy = 50%`,
 *    `refR = 50%` (50% of the bbox WIDTH — JointJS resolves refR against width),
 *    stroke `#333333`, strokeWidth 2 (inherited), but `merode.InitialState`
 *    OVERRIDES `body/fill` to `#333333` — a solid dark dot. No label.
 *  - State (`standard.Ellipse`): an ellipse, `refRx/refRy = 50%` (width/2,
 *    height/2), centred; stroke `#333333`, fill `#FFFFFF`, but
 *    `merode.State` OVERRIDES `body/strokeWidth` to 1 (the default is 2). Label
 *    centred, Arial 14px, fill `#333333`.
 *  - FinalState (`joint.dia.Element`): an OUTER ellipse at `refRx/refRy = 50%`
 *    and an INNER ellipse at `refRx/refRy = 45%`, both centred, stroke `#333333`,
 *    strokeWidth 1, fill `#FFFFFF`. Label centred, Arial 14px, fill `#333333`.
 *
 * JointJS ref-* percentages are fractions of the node bounding box; we reproduce
 * them by multiplying by the node width/height the uimodel persisted (a State
 * node defaults to 100x50). The SVG fills the React Flow node box exactly, with
 * `overflow: visible` so the centred stroke is never clipped (JointJS never
 * clipped the body).
 *
 * The FSM label font matches the JointJS `standard.*` label default — the same
 * `textVerticalAnchor: 'middle'`, `textAnchor: 'middle'`, `fontSize: 14`,
 * `fill: '#333333'` the shapes carry. The font-family is the SVG default JointJS
 * used (no family set on the FSM label), so we leave it to the inherited page
 * font, matching the old paper. Multi-line state names are not used (states have
 * single-word names), so a single centred line is faithful.
 *
 * These are RENDER-ONLY (6d step 1): no drag, no resize, no connect handles
 * beyond the structural source+target React Flow needs so edges resolve.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';

export type FsmStateType = 'INITIAL_STATE' | 'STATE' | 'FINAL_STATE';

/**
 * Distinct handle ids so React Flow Loose mode can accept a same-node drop
 * (`handleId !== fromHandleId`). Without ids both handles are `null` and a
 * self-loop is always invalid.
 */
export const FSM_SOURCE_HANDLE_ID = 'source';
export const FSM_TARGET_HANDLE_ID = 'target';

/**
 * Highlight class applied to the node group, mirroring the JointJS
 * `joint-highlighted` class the old FSM paper applied to a highlighted/selected
 * cell (FSM only used the single orange highlight — see components/FSM/styles.scss
 * `g.joint-highlighted`). null = no highlight.
 */
export type FsmHighlightClass = 'joint-highlighted' | null;

export type FsmNodeData = {
  /** State type — picks the shape. */
  stateType: FsmStateType;
  /** State name (the centred label; INITIAL_STATE has none). */
  name: string;
  /** Node width in px (from the uimodel node size). */
  width: number;
  /** Node height in px (from the uimodel node size). */
  height: number;
  /** Highlight class, or null. */
  highlight: FsmHighlightClass;
  /**
   * Whether the node is a live link endpoint (the add_transition tool is armed).
   * When true the structural handles become full-node CONNECT handles so a
   * node-to-node drag (incl. onto the SAME node — a self-transition) creates a
   * transition (the Rappid full-node magnet). When false they stay inert.
   */
  connectable?: boolean;
  [key: string]: unknown;
};

/** React Flow node type alias for an FSM state node. */
export type FsmFlowNode = Node<FsmNodeData, 'fsmState'>;

// JointJS FSM shapes (rappid.min.js): InitialState (standard.Circle) keeps the
// inherited strokeWidth 2; State / FinalState override it to 1.
const STATE_STROKE = '#333333';
const STATE_FILL = '#FFFFFF';
const INITIAL_FILL = '#333333';
const STATE_STROKE_WIDTH = 1;
const INITIAL_STROKE_WIDTH = 2;
const LABEL_FONT_SIZE = 14;
const LABEL_FILL = '#333333';
// FinalState inner ring radius factor (45% vs the outer 50%).
const FINAL_INNER_FACTOR = 0.45 / 0.5; // inner refR 45% / outer refR 50% = 0.9

/**
 * Structural-only handle: invisible, zero-size, centred, non-interactive. Present
 * solely so React Flow's edge geometry resolves (an edge is dropped unless both
 * endpoints expose a source + target handle); the visible boundary anchoring is
 * our own ellipse math (edgeGeometry.ts). Same pattern as ObjectTypeNode.
 */
const HIDDEN_HANDLE_STYLE: React.CSSProperties = {
  width: 1,
  height: 1,
  minWidth: 0,
  minHeight: 0,
  background: 'transparent',
  border: 'none',
  opacity: 0,
  pointerEvents: 'none',
  left: '50%',
  top: '50%',
};

/**
 * Full-node CONNECT handle style: covers the entire node so a node-to-node drag
 * starts/lands anywhere on it (the Rappid full-node magnet). Invisible — only the
 * cursor signals connectability. Used only while the add_transition tool is armed.
 * Same pattern as ObjectTypeNode (CONNECT_HANDLE_STYLE).
 */
const CONNECT_HANDLE_STYLE: React.CSSProperties = {
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  transform: 'none',
  borderRadius: 0,
  background: 'transparent',
  border: 'none',
  opacity: 0,
};

function StructuralHandles({ connectable }: { connectable: boolean }) {
  // Connect-armed: both handles become full-node magnets so a drag from/to
  // anywhere on the node (incl. back onto itself — a self-transition) lands.
  // Otherwise they stay inert structural anchors so existing edges still resolve.
  return (
    <>
      <Handle
        id={FSM_TARGET_HANDLE_ID}
        type="target"
        position={Position.Top}
        isConnectable={connectable}
        className={connectable ? 'merode-connect-handle' : undefined}
        style={connectable ? CONNECT_HANDLE_STYLE : HIDDEN_HANDLE_STYLE}
      />
      <Handle
        id={FSM_SOURCE_HANDLE_ID}
        type="source"
        position={Position.Bottom}
        isConnectable={connectable}
        className={connectable ? 'merode-connect-handle' : undefined}
        style={connectable ? CONNECT_HANDLE_STYLE : HIDDEN_HANDLE_STYLE}
      />
    </>
  );
}

function FsmStateNode({ data }: NodeProps<FsmFlowNode>) {
  const { stateType, name, width, height, highlight, connectable } = data;
  const groupClass = ['merode-fsm-state', highlight].filter(Boolean).join(' ');

  const cx = width / 2;
  const cy = height / 2;
  // JointJS resolves refR (circle) against the bbox WIDTH; refRx/refRy (ellipse)
  // against width/height respectively. So a circle radius is width/2 and an
  // ellipse uses width/2 x height/2.
  const rx = width / 2;
  const ry = height / 2;
  const circleR = width / 2;

  const label =
    stateType !== 'INITIAL_STATE' && name ? (
      <text
        className="merode-fsm-label"
        x={cx}
        y={cy}
        fill={LABEL_FILL}
        fontSize={LABEL_FONT_SIZE}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {name}
      </text>
    ) : null;

  return (
    <>
      <StructuralHandles connectable={connectable === true} />
      <svg
        className={groupClass}
        width={width}
        height={height}
        style={{ overflow: 'visible', display: 'block' }}
      >
        {stateType === 'INITIAL_STATE' ? (
          // Filled dark dot. standard.Circle default stroke #333333 / strokeWidth
          // 2 stays; merode.InitialState overrides only the fill to #333333.
          <circle
            className="merode-fsm-body"
            cx={cx}
            cy={cy}
            r={circleR}
            fill={INITIAL_FILL}
            stroke={STATE_STROKE}
            strokeWidth={INITIAL_STROKE_WIDTH}
          />
        ) : stateType === 'FINAL_STATE' ? (
          <>
            {/* Outer ring (refRx/refRy 50%). */}
            <ellipse
              className="merode-fsm-body"
              cx={cx}
              cy={cy}
              rx={rx}
              ry={ry}
              fill={STATE_FILL}
              stroke={STATE_STROKE}
              strokeWidth={STATE_STROKE_WIDTH}
            />
            {/* Inner ring (refRx/refRy 45% => 90% of the outer radii). */}
            <ellipse
              className="merode-fsm-innerbody"
              cx={cx}
              cy={cy}
              rx={rx * FINAL_INNER_FACTOR}
              ry={ry * FINAL_INNER_FACTOR}
              fill={STATE_FILL}
              stroke={STATE_STROKE}
              strokeWidth={STATE_STROKE_WIDTH}
            />
          </>
        ) : (
          // Normal state: single ellipse, strokeWidth 1.
          <ellipse
            className="merode-fsm-body"
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill={STATE_FILL}
            stroke={STATE_STROKE}
            strokeWidth={STATE_STROKE_WIDTH}
          />
        )}
        {label}
      </svg>
    </>
  );
}

export default FsmStateNode;
